import ReactDOM from 'react-dom';
import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import {
  Text,
  SelectField,
  InfoBox,
  ToggleSwitch,
  InfoBoxProps,
  SearchField,
  SearchFieldProps,
} from '@oneaudi/unified-web-components';
import { OneCMSContent } from '../../../types/oneCMS';
import {
  initializeAutocomplete,
  useServicesContext,
  setAddressFromLatLngGeocoder,
  getScsUrlPartsFromConfiguration,
  FilterContext,
  initializeInitialStateFromLocalStorage,
  updateLocationSearchInputLocalStorage,
  FilterDTO,
  useApplyFilters,
  useClearAllDealerFilters,
  shouldBeUsedFilterDataWithoutClearingDealers,
  FilterContextValue,
  setLocationFilterToDefaultState,
  trackGiveConsent,
  trackRemoveConsent,
  DynamicComponent,
  isLegacy,
  dynamicStylingHelper,
  useGoogleMaps,
  useMandatoryAreaSearchContext,
  MandatoryAreaSearchState,
} from '@oneaudi/vtp-shared';
import {
  SearchWrapper,
  LocateMeButton,
  QuickLocationFilterWrapper,
} from '../styles/QuickLocationFilter.styles';
import { ExpiringStorage } from '@oneaudi/stck-store';
import { SYS_SPACE_FIXED_150, SYS_SPACE_FIXED_300 } from '@oneaudi/unified-web-common';
import { SubHeadText } from '../styles/SwitchNCUC.styles';
import styled, { createGlobalStyle } from 'styled-components';
import { ConsentModal, saveConsentToStorage } from '@oneaudi/two-click';
import { LayerHandleV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { Notification } from '@oneaudi/unified-web-alpha-components';
import { SYS_COLOR_TEXT_COPY_DEFAULT_DARK } from '@oneaudi/unified-web-alpha-common';
import { FeatureServiceDependencies } from '../../../FeatureHubAppDefinition';

export interface QuickLocationFilterProps {}

interface PlaceAutocompleteElementWithValue extends google.maps.places.PlaceAutocompleteElement {
  value: string;
}

export const QuickLocationFilter: React.FC<QuickLocationFilterProps> = () => {
  const [locateMeError, setLocateMeError] = useState<string>('');
  const { getFeatureServices, getConfiguration, getEnvironmentConfig } = useServicesContext();
  const {
    'layer-manager': layerManager,
    'audi-content-service': audiContentService,
    'audi-tracking-service': trackingService,
    'gfa:locale-service': localeService,
    's2:logger': logger,
  } = getFeatureServices<FeatureServiceDependencies>();
  const { mileageUnit, locationFilterConfig_radiusOptions, locationFilterConfig_defaultRadius } =
    audiContentService.getContent().fields as OneCMSContent;

  const scsUrlParts = getScsUrlPartsFromConfiguration(getConfiguration()!, getEnvironmentConfig()!);
  const country = localeService.countryCode;
  const {
    enableMandatoryAreaSearch,
    mandatoryAreaSearchState,
    openMandatoryAreaSearchModal,
    setMandatoryAreaSearchLocationSelected,
    requireMandatoryAreaSearchLocation,
  } = useMandatoryAreaSearchContext();

  const localStorageLocationSearchInputKey = enableMandatoryAreaSearch
    ? `VTP_${scsUrlParts?.marketPath}_mandatoryLocationSearch--input`
    : `VTP_${scsUrlParts?.marketPath}_locationSearch--input`;
  const radiusOptions = locationFilterConfig_radiusOptions || [10, 20, 50, 100, 200];
  const defaultRadius = locationFilterConfig_defaultRadius || 10;

  const locationSearchHeadlineLabel = useI18n({
    id: `nemo.sc.plp.quicklocationfilter.locationSearchInputLabel`,
    defaultMessage: 'Location',
  });
  const locationSearchDefaultInputLabel = useI18n({
    id: `nemo.sc.plp.quickLocationFilter.locationSearchDefaultInputLabel`,
    defaultMessage: '',
  });
  const locationSearchInputLabel = useI18n({
    id: `nemo.sc.plp.quicklocationfilter.locationSearchDescriptionLabel`,
    defaultMessage: 'Enter city or postcode',
  });
  const distanceRadiusLabel = useI18n({
    id: `nemo.sc.plp.electrofiltration.distanceRadiusLabel`,
    defaultMessage: 'Distance',
  });
  const locationRequiredNotificationCopy = useI18n({
    id: 'nemo.sc.plp.quickLocationFilter.locationRequiredNotification',
    defaultMessage: 'Location required: Please add a location to continue your search',
  });

  const clearLocationSearchInputButtonAriaLabel = useI18n({
    id: `nemo.sc.plp.locationfilter.clearLocationSearchInputButtonAriaLabel`,
    defaultMessage: 'Clear the text in the location search input field.',
  });
  const locateMeButtonAriaLabel = useI18n({
    id: `nemo.sc.plp.locationfilter.cookieConsentRequestMap.locateMeButtonAriaLabel`,
    defaultMessage: 'Use your location.',
  });
  const consentModalHeadline = useI18n({
    id: `google-maps-two-click-consent.headline`,
    defaultMessage: '',
  });
  const consentModalBody = useI18n({
    id: `google-maps-two-click-consent.copyText`,
    defaultMessage: '',
  });
  const consentModalToggle = useI18n({
    id: `google-maps-two-click-consent.toggleLabel`,
    defaultMessage: '',
  });
  const toggleText = useI18n({
    id: `nemo.sc.filter.locationfilter.cookieConsentControl.Toggle`,
    defaultMessage: '',
  });
  const grantConsentBannerHeadline = useI18n({
    id: `nemo.sc.plp.quickLocationFilter.cookieConsentHeadline`,
    defaultMessage: '',
  });
  const grantConsentBannerText = useI18n({
    id: `nemo.sc.plp.quickLocationFilter.cookieConsentText`,
    defaultMessage: '',
  });
  const grantConsentBannerButton = useI18n({
    id: `nemo.sc.plp.quickLocationFilter.cookieConsentButton`,
    defaultMessage: '',
  });

  const applyFilters = useApplyFilters();
  const clearAllDealerFilters = useClearAllDealerFilters();

  const localStorage = new ExpiringStorage(localStorageLocationSearchInputKey, 30);
  const inputDataFromLocalStorage = localStorage.readEntry();

  const context = React.useContext(FilterContext) as FilterContextValue;
  const {
    radius,
    setRadius,
    searchedCoords,
    prevSearchedCoords,
    setWholeMarketDealersFetch,
    setSearchedCoords,
    setPrevSearchedCoords,
    resetLocationSearch,
    setResetLocationSearch,
  } = context;
  const locationFilterResetContext = useMemo(
    () => ({
      setWholeMarketDealersFetch,
      setRadius,
      setPrevSearchedCoords,
      setSearchedCoords,
    }),
    [setPrevSearchedCoords, setRadius, setSearchedCoords, setWholeMarketDealersFetch]
  );

  const [isClient, setIsClient] = React.useState(false);
  const { needConsent, effectiveConsent, libraryLoaded } = useGoogleMaps();

  const autocompleteRef = useRef<PlaceAutocompleteElementWithValue | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevStateRadius = useRef<any>(null);
  const changedByLocateMe = useRef<boolean>(false);
  const [locationInputValue, setLocationInputValue] = useState<string>('');

  const clearSelectedLocation = useCallback(() => {
    setLocationInputValue('');
    setLocationFilterToDefaultState(
      getConfiguration()!,
      getEnvironmentConfig()!,
      clearAllDealerFilters,
      locationFilterResetContext,
      inputRef,
      enableMandatoryAreaSearch,
      requireMandatoryAreaSearchLocation
    );
  }, [
    clearAllDealerFilters,
    enableMandatoryAreaSearch,
    getConfiguration,
    getEnvironmentConfig,
    locationFilterResetContext,
    requireMandatoryAreaSearchLocation,
  ]);

  const handleLocateMeClick = () => {
    changedByLocateMe.current = true;

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
    };

    const successCallback = (position: GeolocationPosition) => {
      setAddressFromLatLngGeocoder(
        position,
        inputRef,
        localStorageLocationSearchInputKey,
        setLocationInputValue,
        context,
        autocompleteRef
      );
      setLocateMeError('');
    };

    const errorCallback = (error: GeolocationPositionError) => {
      logger?.error(error);
      setLocateMeError(error?.message || 'Unable to get location. Please enter it manually');
    };

    navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
  };

  const [twoClickOverlayOpened, setIsTwoClickOverlayOpened] = React.useState<boolean>(false);
  const [focusLayerNode, setFocusLayerNode] = React.useState<Element>();
  const [focusLayer, setFocusLayer] = React.useState<LayerHandleV1<{}>>();
  const focusLayerRef = React.useCallback(
    (node: any) => {
      setFocusLayerNode(node);
    },
    [setFocusLayerNode]
  );

  const openConsentOverlay = () => {
    const layer = layerManager.openFocusLayer(
      () => <div ref={focusLayerRef} />,
      {},
      {
        userCloseable: true,
        size: FocusLayerSizeV2.B,
      }
    );
    setFocusLayer(layer);
    setIsTwoClickOverlayOpened(true);
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!inputRef.current || !libraryLoaded || !effectiveConsent) {
      return () => undefined;
    }

    const cleanupAutocomplete = initializeAutocomplete(
      country,
      inputRef,
      localStorageLocationSearchInputKey,
      setSearchedCoords,
      autocompleteRef,
      enableMandatoryAreaSearch,
      logger,
      setLocationInputValue,
      (value: string) => {
        if (!value.trim()) {
          clearSelectedLocation();
        }
      }
    );

    if (inputDataFromLocalStorage) {
      setWholeMarketDealersFetch(false);
      initializeInitialStateFromLocalStorage(
        inputRef,
        inputDataFromLocalStorage,
        setRadius,
        setSearchedCoords,
        logger
      );
      setLocationInputValue(inputDataFromLocalStorage.inputName);
    } else {
      setWholeMarketDealersFetch(true);
      setLocationInputValue('');
    }
    setLocateMeError('');

    return () => {
      cleanupAutocomplete?.();
    };
  }, [
    clearSelectedLocation,
    country,
    effectiveConsent,
    enableMandatoryAreaSearch,
    inputDataFromLocalStorage?.inputName,
    inputDataFromLocalStorage?.placeId,
    inputDataFromLocalStorage?.radius,
    libraryLoaded,
    localStorageLocationSearchInputKey,
    logger,
    setRadius,
    setSearchedCoords,
    setWholeMarketDealersFetch,
  ]);

  useEffect(() => {
    const inputElement = inputRef.current;

    if (!inputElement) {
      return () => undefined;
    }

    const handleInput = () => {
      if (!inputElement.value.trim()) {
        clearSelectedLocation();
      }
    };

    inputElement.addEventListener('input', handleInput);

    return () => {
      inputElement.removeEventListener('input', handleInput);
    };
  }, [clearSelectedLocation]);

  useEffect(() => {
    const savedValue = new ExpiringStorage(localStorageLocationSearchInputKey, 30).readEntry()
      ?.inputName;

    if (!searchedCoords && savedValue === undefined) {
      setLocationInputValue('');

      if (inputRef.current) {
        inputRef.current.value = '';
      }

      if (libraryLoaded && autocompleteRef.current) {
        autocompleteRef.current.value = '';
      }

      return;
    }

    if (savedValue === undefined) {
      return;
    }

    setLocationInputValue(savedValue);

    if (inputRef.current) {
      inputRef.current.value = savedValue;
    }

    if (libraryLoaded && autocompleteRef.current) {
      autocompleteRef.current.value = savedValue;
    }
  }, [libraryLoaded, localStorageLocationSearchInputKey, searchedCoords]);

  useEffect(() => {
    if (searchedCoords && !radius) {
      setMandatoryAreaSearchLocationSelected();
      setRadius(String(defaultRadius));
    } else if (searchedCoords && radius && inputRef.current) {
      if (!isClient) return;
      if (!radius) setRadius(String(defaultRadius));

      setMandatoryAreaSearchLocationSelected();

      const firstAutocompleteSearch = prevStateRadius.current === null;
      const firedByChangedRadius = prevStateRadius.current !== radius;
      const doubleSearchOfSameLocation = prevSearchedCoords?.latitude === searchedCoords.latitude;

      const geoFilterDTO: FilterDTO = {
        id: 'geo',
        active: true,
        type: 'location-filter',
        values: [
          searchedCoords?.latitude as any,
          searchedCoords?.longitude as any,
          radius,
          mileageUnit,
          inputRef.current.value,
        ],
        preset: enableMandatoryAreaSearch,
      };

      const requestData = async () => {
        // eslint-disable-next-line no-unused-expressions
        shouldBeUsedFilterDataWithoutClearingDealers(firedByChangedRadius, firstAutocompleteSearch)
          ? await applyFilters(geoFilterDTO)
          : await clearAllDealerFilters(geoFilterDTO);
      };

      if (!doubleSearchOfSameLocation || firedByChangedRadius) requestData().catch(logger?.error);

      prevStateRadius.current = radius;

      changedByLocateMe.current = false;
      setPrevSearchedCoords({
        latitude: searchedCoords?.latitude,
        longitude: searchedCoords?.longitude,
      });
      setWholeMarketDealersFetch(false);
    }
  }, [
    searchedCoords,
    radius,
    isClient,
    defaultRadius,
    mileageUnit,
    enableMandatoryAreaSearch,
    applyFilters,
    clearAllDealerFilters,
    logger,
    prevSearchedCoords?.latitude,
    setMandatoryAreaSearchLocationSelected,
    setPrevSearchedCoords,
    setRadius,
    setWholeMarketDealersFetch,
  ]);

  useEffect(() => {
    if (radius) {
      updateLocationSearchInputLocalStorage(localStorageLocationSearchInputKey, 'radius', radius);
    }
  }, [radius]);

  useEffect(() => {
    const savedValue = inputDataFromLocalStorage?.inputName || '';
    setLocationInputValue(savedValue);
    if (inputRef.current) {
      inputRef.current.value = savedValue;
    }
    if (libraryLoaded && autocompleteRef.current) {
      autocompleteRef.current.value = savedValue;
      autocompleteRef.current.setAttribute('placeholder', locationSearchDefaultInputLabel);
    }
  }, [inputDataFromLocalStorage?.inputName, libraryLoaded]);

  useEffect(() => {
    if (effectiveConsent) {
      focusLayer?.close();
      setIsTwoClickOverlayOpened(false);
    }
  }, [effectiveConsent]);

  useEffect(() => {
    if (resetLocationSearch) {
      prevStateRadius.current = null;
      setResetLocationSearch(false);
    }
  }, [resetLocationSearch]);

  return (
    <QuickLocationFilterWrapper>
      <SubHeadText
        tag="p"
        variant={{
          type: 'copy',
          order: dynamicStylingHelper('2', '1'),
          style: dynamicStylingHelper('short', 'normal'),
        }}
        margin={[0, 0, SYS_SPACE_FIXED_150, 0]}
      >
        {locationSearchHeadlineLabel}
      </SubHeadText>
      {!effectiveConsent && (
        <StyledInfoBox
          title={{
            tag: 'h2',
            text: grantConsentBannerHeadline,
          }}
          copy={grantConsentBannerText}
          margin={[0, 0, '28px', 0]}
          textLink={{
            label: grantConsentBannerButton,
            onClick: () => {
              openConsentOverlay();
              setIsTwoClickOverlayOpened(true);
              trackGiveConsent(trackingService, grantConsentBannerButton);
            },
          }}
        />
      )}
      <>
        {twoClickOverlayOpened &&
          focusLayerNode &&
          ReactDOM.createPortal(
            <>
              <TwoClickStyle />
              <ConsentModal
                theme="light"
                headerText={consentModalHeadline}
                bodyText={consentModalBody}
                toggleText={consentModalToggle}
                showCloseButton
                showRedirectButton={false}
                closeButtonCallback={() => {
                  focusLayer?.close();
                  setIsTwoClickOverlayOpened(false);
                }}
                twoClickStyle={{
                  position: 'relative',
                  padding: 0,
                  top: 'unset',
                  transform: 'unset',
                  maxHeight: 'unset',
                  width: 'unset',
                  left: 'unset',
                }}
                twoClickCancelCtaStyle={{
                  display: 'none',
                }}
                vtp
              />
            </>,
            focusLayerNode
          )}
      </>
      <SearchWrapper>
        <StyledSearchField
          inputRef={inputRef}
          onInputChange={(value: string) => {
            setLocationInputValue(value);
            if (!value.trim()) {
              clearSelectedLocation();
            }
          }}
          additionalIconButton={{
            onClick: () => {
              if (isLegacy && inputRef?.current) {
                inputRef.current.value = '';
                clearSelectedLocation();
              } else {
                handleLocateMeClick();
              }
            },
            icon: dynamicStylingHelper('cancel', 'gps'),
            'aria-label': dynamicStylingHelper(
              clearLocationSearchInputButtonAriaLabel,
              locateMeButtonAriaLabel
            ),
          }}
          value={locationInputValue}
          id="quickLocationFilterInput"
          label={locationSearchDefaultInputLabel}
          placeholder={locationSearchDefaultInputLabel}
          clearButton={{
            'aria-label': clearLocationSearchInputButtonAriaLabel,
            onClick: clearSelectedLocation,
          }}
          disabled={!effectiveConsent}
        />
        <DynamicComponent
          legacy={
            <LocateMeButton
              variant="secondary"
              icon="gps"
              size="small"
              disabled={!effectiveConsent}
              onClick={() => handleLocateMeClick()}
              aria-label={locateMeButtonAriaLabel}
            />
          }
        />
        {enableMandatoryAreaSearch &&
          mandatoryAreaSearchState === MandatoryAreaSearchState.ENABLED && (
            <Notification
              data-testid="quick-location-required-notification"
              copy={locationRequiredNotificationCopy}
              icon="caution"
              margin={[0, 0, SYS_SPACE_FIXED_300, 0]}
            />
          )}
        {locateMeError ? <InfoBox key="locateMeError" copy={locateMeError} icon="caution" /> : null}
      </SearchWrapper>
      <DynamicComponent
        legacy={
          <Text
            variant={{ type: 'copy', style: 'normal', order: '2' }}
            margin={[0, 0, SYS_SPACE_FIXED_300, 0]}
          >
            {locationSearchInputLabel}
          </Text>
        }
      />
      <SelectField
        id="select_radius"
        label={distanceRadiusLabel}
        onChange={(e: any) => {
          setRadius(e.target.value);
        }}
        value={radius!}
        disabled={!effectiveConsent || !radius}
      >
        {radiusOptions.map((radiusOption, index) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <option value={radiusOption} key={index}>
              + {radiusOption} {mileageUnit || 'km'}
            </option>
          );
        })}
      </SelectField>
      {effectiveConsent && needConsent && (
        <ToggleSwitch
          id="quick_location_filter_cookie_consent"
          label={toggleText}
          labelPosition="trailing"
          margin={[SYS_SPACE_FIXED_300, 0, 0, 0]}
          name="toggle-switch"
          onChange={() => {
            // set location filter to default
            setLocationFilterToDefaultState(
              getConfiguration()!,
              getEnvironmentConfig()!,
              clearAllDealerFilters,
              context,
              inputRef,
              enableMandatoryAreaSearch,
              requireMandatoryAreaSearchLocation
            );
            // remove consent
            saveConsentToStorage(false);
            // tracking event
            trackRemoveConsent(trackingService);
            // open MAS overlay
            if (enableMandatoryAreaSearch) {
              openMandatoryAreaSearchModal();
            }
          }}
          value="toggle-switch"
          checked={effectiveConsent}
        />
      )}
    </QuickLocationFilterWrapper>
  );
};

export const StyledSearchField = styled((props) => <SearchField {...props} />)<SearchFieldProps>`
  min-width: unset;
  > div > div {
    // (locate me on top)
    z-index: 10;
  }
`;

export const StyledInfoBox = styled((props) => <InfoBox {...props} />)<InfoBoxProps>``;

const TwoClickStyle = createGlobalStyle`
  .two-click-alpha {
    box-shadow: none!important;
    a {
      color: ${dynamicStylingHelper('#333333', SYS_COLOR_TEXT_COPY_DEFAULT_DARK)};
    }
  }
`;
